import { ReactNode } from "react";
import { Button, Card } from "react-bootstrap";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import { PageTabs, type PageTab } from "./PageTabs";
import "./CatalogIndex.css";

export interface AdminFormPageProps {
  title: ReactNode;
  /** Tab whose form Save should submit. Omit (or leave undefined while a
   *  no-form tab like a list/slideout-driven tab is active) to hide the Save
   *  button entirely — see `tabs`. */
  formId?: string;
  /** Omit to render no Cancel button — e.g. a page with nowhere to go back to. */
  onCancel?: () => void;
  onSaveAndAddAnother?: () => void;
  extraActions?: ReactNode;
  isSubmitting?: boolean;
  /** Disables Save without isSubmitting's spinner, e.g. while the page shows
   *  its own loading state. */
  saveDisabled?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  /** When provided, renders a tab bar inside the card below the header —
   *  the header (title/Cancel/Save) stays mounted across tab changes; only
   *  `children` (and `formId`, per the currently active tab) should change. */
  tabs?: PageTab[];
  activeTab?: string;
  onTabChange?: (key: string) => void;
  children: ReactNode;
}

export const AdminFormPage = ({
  title,
  formId,
  onCancel,
  onSaveAndAddAnother,
  extraActions,
  isSubmitting = false,
  saveDisabled = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  tabs,
  activeTab,
  onTabChange,
  children,
}: AdminFormPageProps) => {
  return (
    <div className="admin-form-card legacyPageBleed">
      <Card>
        <Card.Header>
          <div className="admin-form-card__row">
            <div
              className={
                onCancel
                  ? "admin-form-card__title-group"
                  : "admin-form-card__title-group admin-form-card__title-group--no-cancel"
              }
            >
              {onCancel && (
                <Button
                  className="btnSeawhite"
                  type="button"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  {cancelLabel}
                </Button>
              )}
              <span className="adminFormCardTitle">{title}</span>
            </div>
            <div className="admin-form-card__actions">
              {extraActions}
              {onSaveAndAddAnother && (
                <Button
                  data-testid="save-and-add-another-button"
                  className="btnSeawhite"
                  type="button"
                  onClick={onSaveAndAddAnother}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <SmallSpinner /> : "Save and Add Another"}
                </Button>
              )}
              {formId && (
                <Button
                  data-testid="save-button"
                  className="squareGreenButton"
                  type="submit"
                  form={formId}
                  disabled={isSubmitting || saveDisabled}
                >
                  {isSubmitting ? <SmallSpinner /> : saveLabel}
                </Button>
              )}
            </div>
          </div>
          <hr />
        </Card.Header>
        {tabs && (
          <PageTabs
            activeKey={activeTab ?? ""}
            onSelect={(key) => onTabChange?.(key)}
            tabs={tabs}
          />
        )}
        <Card.Body style={{ padding: "0px 16px 16px" }}>{children}</Card.Body>
      </Card>
    </div>
  );
};
